import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getSessionData } from "utils/common";
import { showMessage } from "../common.slice";

const initialState = {
  settingsGroupOptions: [],
  settingsGroupList: [],
  settingsGroupLoading: false,
  isSettingsGroupUpdated: false,
  selectedSettingsGroup: null,
  packageSummary: null,
  packageSummaryLoading: false,
};

export const getSettingsGroupOptions = createAsyncThunk(
  "settingsGroup/getOptions",
  (_, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get("settings/settings-group/get-options")
        .then(({ data }) => {
          if (data.status === 200) resolve(data.data || []);
          else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

export const getSettingsGroupList = createAsyncThunk(
  "settingsGroup/getAll",
  (_, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get("settings/settings-group/get-all")
        .then(({ data }) => {
          if (data.status === 200) resolve(data.data || []);
          else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

export const getSettingsGroupById = createAsyncThunk(
  "settingsGroup/getById",
  (id, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`settings/settings-group/get-by-id/${id}`)
        .then(({ data }) => {
          if (data.status === 200) resolve(data.data);
          else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

export const getSettingsGroupPackageSummary = createAsyncThunk(
  "settingsGroup/getPackageSummary",
  (userId, { dispatch }) => {
    return new Promise((resolve, reject) => {
      if (!userId) {
        resolve(null);
        return;
      }
      axios
        .get(`settings/settings-group/get-package-summary/${userId}`)
        .then(({ data }) => {
          if (data.status === 200) resolve(data.data);
          else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

export const saveSettingsGroup = createAsyncThunk(
  "settingsGroup/save",
  (formData, { dispatch }) => {
    return new Promise((resolve, reject) => {
      const { userId } = getSessionData();
      const payload = {
        createdBy: userId,
        id: formData.id || 0,
        name: formData.name?.trim(),
        description: formData.description?.trim() || "",
        isActive: formData.isActive !== false,
        templateUserId: formData.templateUserId?.value || formData.templateUserId,
        segmentIds: (formData.segmentIds || []).map((s) => s.value || s),
      };
      axios
        .post("settings/settings-group/save", payload)
        .then(({ data }) => {
          if (data.status === 200) {
            dispatch(showMessage({ message: data.message, varient: "success" }));
            resolve(data);
          } else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

export const deleteSettingsGroup = createAsyncThunk(
  "settingsGroup/delete",
  (id, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .delete(`settings/settings-group/delete/${id}`)
        .then(({ data }) => {
          if (data.status === 200) {
            dispatch(showMessage({ message: data.message, varient: "success" }));
            resolve(data);
          } else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: error?.response?.data?.message || error.message }));
          reject(error);
        });
    });
  }
);

const settingsGroupSlice = createSlice({
  name: "settingsGroup",
  initialState,
  reducers: {
    setIsSettingsGroupUpdated: (state, action) => {
      state.isSettingsGroupUpdated = action.payload;
    },
    setSelectedSettingsGroup: (state, action) => {
      state.selectedSettingsGroup = action.payload;
    },
    clearSettingsGroupOptions: (state) => {
      state.settingsGroupOptions = [];
    },
    clearPackageSummary: (state) => {
      state.packageSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSettingsGroupOptions.fulfilled, (state, action) => {
        state.settingsGroupOptions = (action.payload || []).map((g) => ({
          value: g.id,
          label: g.name,
          templateUserId: g.templateUserId,
        }));
      })
      .addCase(getSettingsGroupList.pending, (state) => {
        state.settingsGroupLoading = true;
      })
      .addCase(getSettingsGroupList.fulfilled, (state, action) => {
        state.settingsGroupLoading = false;
        state.settingsGroupList = action.payload || [];
      })
      .addCase(getSettingsGroupList.rejected, (state) => {
        state.settingsGroupLoading = false;
      })
      .addCase(getSettingsGroupById.fulfilled, (state, action) => {
        state.selectedSettingsGroup = action.payload;
      })
      .addCase(getSettingsGroupPackageSummary.pending, (state) => {
        state.packageSummaryLoading = true;
      })
      .addCase(getSettingsGroupPackageSummary.fulfilled, (state, action) => {
        state.packageSummaryLoading = false;
        state.packageSummary = action.payload;
      })
      .addCase(getSettingsGroupPackageSummary.rejected, (state) => {
        state.packageSummaryLoading = false;
        state.packageSummary = null;
      })
      .addCase(saveSettingsGroup.fulfilled, (state) => {
        state.isSettingsGroupUpdated = true;
      })
      .addCase(deleteSettingsGroup.fulfilled, (state) => {
        state.isSettingsGroupUpdated = true;
      });
  },
});

export const {
  setIsSettingsGroupUpdated,
  setSelectedSettingsGroup,
  clearSettingsGroupOptions,
  clearPackageSummary,
} = settingsGroupSlice.actions;

export default settingsGroupSlice.reducer;
